import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUrlRuleset } from '../hooks/useUrlRuleset';
import { useApplyCharacterDraft } from '../hooks/useApplyCharacterDraft';
import { useCharacterCreationStore } from '../stores/characterCreationStore';
import { getRulesetFeatures } from '@character-creator/data';
import { saveCharacter } from '../utils/characterDatabase';
import { hasCharacterData } from '../utils/draftUtils';
import type { CharacterDraft } from '../stores/characterCreationStore';
import type { RuntimeState } from '../utils/characterExport';
import { DiceIcon, IgnusLogo } from '../components/ui/Icons';
import { ImportCharacterDialog } from '../components/ui/ImportCharacterDialog';

export function RulesetLandingPage() {
  const { rulesetId, config } = useUrlRuleset();
  const features = getRulesetFeatures(rulesetId);
  const { applyRandomCharacter, applyImportedCharacter } = useApplyCharacterDraft();
  const { draft, reset, setRulesetId } = useCharacterCreationStore();
  const navigate = useNavigate();
  const [showImportDialog, setShowImportDialog] = useState(false);

  const isIgnus = rulesetId === 'ignus';
  const terminology = features.ui.terminology;

  const handleImport = (imported: CharacterDraft, runtimeState?: RuntimeState) => {
    applyImportedCharacter(imported, rulesetId, runtimeState);
  };

  const handleNewCharacter = async () => {
    if (hasCharacterData(draft)) {
      await saveCharacter(draft);
    }
    reset();
    setRulesetId(rulesetId);
    navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
  };

  const handleSurpriseMe = () => {
    applyRandomCharacter(rulesetId);
  };

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center py-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          {isIgnus ? (
            <IgnusLogo className="w-12 h-12 text-orange-500" />
          ) : (
            <BookIcon className="w-10 h-10 text-accent-gold" />
          )}
          <h1 className="font-display text-4xl md:text-5xl font-bold text-text-primary">
            {config.name}
          </h1>
        </div>
        <p className="text-text-secondary text-lg max-w-2xl mx-auto mb-8">
          {config.description}
        </p>
        {config.startingLevel && config.startingLevel > 1 && (
          <p className="text-accent-gold text-sm mb-8">
            Characters start at Level {config.startingLevel}
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-4">
          <button
            type="button"
            onClick={handleNewCharacter}
            className="btn-primary text-lg px-8 py-3"
          >
            Create Character
          </button>
          <button
            type="button"
            onClick={handleSurpriseMe}
            className="btn-secondary text-lg px-8 py-3 flex items-center gap-2"
          >
            <DiceIcon className="w-5 h-5" />
            Surprise Me!
          </button>
        </div>
      </section>

      {/* Features */}
      {isIgnus && (
        <section className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            title="Species & Lineages"
            description={`Choose from unique ${terminology.racePlural.toLowerCase()} with distinct ${terminology.subracePlural.toLowerCase()} including Skyborn, Ignan, and modified classic races.`}
            icon={<SpeciesIcon />}
          />
          <FeatureCard
            title="Tiered Feats"
            description="A unique feat system with three tiers, allowing you to trade ability scores for powerful abilities."
            icon={<TieredIcon />}
          />
          <FeatureCard
            title="Faction Context"
            description="Classes are organized by faction: The Circle, The Seat, Civil Society, and The Shunned."
            icon={<FactionIcon />}
          />
        </section>
      )}

      {!isIgnus && (
        <section className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            title="Rules Compliant"
            description="Automatic calculations for ability modifiers, proficiency bonuses, spell slots, and more."
            icon={<RulesIcon />}
          />
          <FeatureCard
            title="Flexible & Forgiving"
            description="Override any rule for homebrew content. The app warns you but lets you play your way."
            icon={<FlexibleIcon />}
          />
          <FeatureCard
            title="Export Anywhere"
            description="Download editable PDFs or JSON files. Take your character anywhere, no account needed."
            icon={<ExportIcon />}
          />
        </section>
      )}

      {/* Quick Actions */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-text-primary mb-6">
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionButton
            onClick={handleNewCharacter}
            title="New Character"
            description="Start from scratch"
          />
          <QuickAction
            to={`/${rulesetId}/races`}
            title={`Browse ${terminology.racePlural}`}
            description="View all options"
          />
          <QuickAction
            to={`/${rulesetId}/classes`}
            title="Browse Classes"
            description="12 classes available"
          />
          <QuickActionButton
            onClick={() => setShowImportDialog(true)}
            title="Import Character"
            description="Load from JSON"
          />
        </div>
      </section>

      {/* Reference Links */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-text-primary mb-6">
          Reference
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            to={`/${rulesetId}/spells`}
            title="Spells"
            description="Browse spellbook"
          />
          <QuickAction
            to={`/${rulesetId}/feats`}
            title="Feats"
            description="View all feats"
          />
          <QuickAction
            to={`/${rulesetId}/conditions`}
            title="Conditions"
            description="Status effects"
          />
          <QuickAction
            to={`/${rulesetId}/docs`}
            title="Documentation"
            description={isIgnus ? 'Setting guide' : 'SRD reference'}
          />
        </div>
      </section>

      {/* Import Dialog */}
      <ImportCharacterDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
      />
    </div>
  );
}

function FeatureCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="card p-6">
      <div className="w-12 h-12 rounded-lg bg-accent-gold/10 text-accent-gold flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-display text-lg font-semibold text-text-primary mb-2">
        {title}
      </h3>
      <p className="text-text-secondary">{description}</p>
    </div>
  );
}

function QuickAction({
  to,
  title,
  description,
}: {
  to: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="card card-hover p-4 flex items-center gap-4 group"
    >
      <div className="w-10 h-10 rounded-lg bg-background-elevated flex items-center justify-center group-hover:bg-accent-gold/10 transition-colors">
        <span className="text-accent-gold text-xl">+</span>
      </div>
      <div>
        <h4 className="font-medium text-text-primary">{title}</h4>
        <p className="text-sm text-text-muted">{description}</p>
      </div>
    </Link>
  );
}

function QuickActionButton({
  onClick,
  title,
  description,
}: {
  onClick: () => void;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card card-hover p-4 flex items-center gap-4 group text-left"
    >
      <div className="w-10 h-10 rounded-lg bg-background-elevated flex items-center justify-center group-hover:bg-accent-gold/10 transition-colors">
        <span className="text-accent-gold text-xl">+</span>
      </div>
      <div>
        <h4 className="font-medium text-text-primary">{title}</h4>
        <p className="text-sm text-text-muted">{description}</p>
      </div>
    </button>
  );
}

// Icons
function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function RulesIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function FlexibleIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function SpeciesIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function TieredIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function FactionIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  );
}
